import React from 'react';

const OverviewTab = ({ project, projectStatuses, getStatusColor, getProgressPercentage }) => {
  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            ${project.estimated_savings_aud?.toLocaleString() || 0}
          </div>
          <div className="text-sm text-blue-600 dark:text-blue-400">Annual Savings (AUD)</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-800">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            ${project.estimated_cost_aud?.toLocaleString() || 0}
          </div>
          <div className="text-sm text-green-600 dark:text-green-400">Implementation Cost (AUD)</div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {project.payback_years || 0} years
          </div>
          <div className="text-sm text-purple-600 dark:text-purple-400">Payback Period</div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/30 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {project.roi_pct?.toFixed(1) || 0}%
          </div>
          <div className="text-sm text-orange-600 dark:text-orange-400">ROI</div>
        </div>
      </div>

      {/* Status Timeline */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Project Progress</h3>
        <div className="relative">
          <div className="flex justify-between mb-2">
            {projectStatuses.map((status, index) => (
              <div key={status} className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                    projectStatuses.indexOf(project.status) >= index
                      ? 'bg-blue-500 text-white dark:bg-blue-600'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                >
                  {index + 1}
                </div>
                <div className="text-xs mt-1 text-center text-gray-700 dark:text-gray-300">
                  {status.replace('_', ' & ')}
                </div>
              </div>
            ))}
          </div>
          <div className="absolute top-4 left-4 right-4 h-1 bg-gray-200 dark:bg-gray-700 -z-10">
            <div
              className="h-full bg-blue-500 dark:bg-blue-600 transition-all duration-300"
              style={{ width: `${getProgressPercentage(project.status)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Project Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Project Details</h3>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Status:</span>
              <span
                className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                  project.status,
                )}`}
              >
                {project.status.replace('_', ' & ').toUpperCase()}
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Priority:</span>
              <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                {project.priority?.toUpperCase()}
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Target Start:</span>
              <span className="ml-2 text-sm text-gray-900 dark:text-white">
                {project.target_start_date || 'Not set'}
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Target Complete:</span>
              <span className="ml-2 text-sm text-gray-900 dark:text-white">
                {project.target_complete_date || 'Not set'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Summary</h3>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Tasks:</span>
              <span className="ml-2 text-sm text-gray-900 dark:text-white">
                {project.project_tasks?.length || 0} tasks
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Grants:</span>
              <span className="ml-2 text-sm text-gray-900 dark:text-white">
                {project.project_grants?.length || 0} linked
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Partner Requests:</span>
              <span className="ml-2 text-sm text-gray-900 dark:text-white">
                {project.project_partner_requests?.length || 0} requests
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">M&V Records:</span>
              <span className="ml-2 text-sm text-gray-900 dark:text-white">
                {project.project_actuals?.length || 0} periods
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <select
          value={project.status}
          onChange={(e) => project.onStatusChange?.(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          {projectStatuses.map((status) => (
            <option key={status} value={status}>
              {status.replace('_', ' & ').toUpperCase()}
            </option>
          ))}
        </select>
        <button className="px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-800">
          Generate PDF Summary
        </button>
        <button className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-800">
          Create Checkout
        </button>
      </div>
    </div>
  );
};

export default OverviewTab;


